import { useState } from 'react';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserRoles } from '@bandido/shared/types';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { CalendarPanel } from './CalendarPanel';
import { PerformanceDashboard } from './PerformanceDashboard';
import { performanceError, usePerformanceAgencies, usePerformanceWorkflows } from './queries';

function PerformanceHomeScreen() {
  const { myProfile } = useAuth();
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const agencyId = Number(params.get('agencyId')) || 0;
  const workflowId = params.get('workflowId') ?? '';
  const [page, setPage] = useState(1);
  const allowed = Boolean(myProfile?.roles?.split(',').includes(UserRoles.ADMINS));
  const agencies = usePerformanceAgencies(myProfile?.id, allowed);
  const validAgency = !agencies.isError && Boolean(agencies.data?.some((agency) => agency.id === agencyId));
  const workflows = usePerformanceWorkflows(myProfile?.id, agencyId, page, allowed && validAgency);

  if (!allowed) return <Alert severity="info">{t('workflowPerformance.denied')}</Alert>;
  return (
    <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto', p: 3, '& input': { fontSize: 16 } }}>
      <Stack spacing={3} sx={{ maxWidth: 1600, mx: 'auto' }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          spacing={2}
        >
          <Typography variant="h4">{t('workflowPerformance.homeTitle')}</Typography>
          {validAgency && myProfile?.id && (
            <CalendarPanel key={`${myProfile.id}:${agencyId}`} userId={myProfile.id} agency={agencyId} />
          )}
        </Stack>
        <Typography>{t('workflowPerformance.selectScope')}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {agencies.isError ? (
            <Alert
              severity="error"
              action={<Button onClick={() => void agencies.refetch()}>{t('workflowPerformance.refresh')}</Button>}
            >
              {t(performanceError(agencies.error))}
            </Alert>
          ) : (
            <TextField
              select
              fullWidth
              label={t('workflowPerformance.agency')}
              value={validAgency ? agencyId : ''}
              disabled={agencies.isLoading}
              onChange={(event) => {
                setPage(1);
                setParams({ agencyId: event.target.value });
              }}
            >
              <MenuItem value="">{t('workflowPerformance.choose')}</MenuItem>
              {agencies.data?.map((agency) => (
                <MenuItem key={agency.id} value={agency.id}>
                  {agency.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {validAgency &&
            (workflows.isError ? (
              <Alert
                severity="error"
                action={<Button onClick={() => void workflows.refetch()}>{t('workflowPerformance.refresh')}</Button>}
              >
                {t(performanceError(workflows.error))}
              </Alert>
            ) : (
              <Stack spacing={1}>
                <TextField
                  select
                  fullWidth
                  label={t('workflowPerformance.workflow')}
                  disabled={workflows.isLoading}
                  value={workflows.data?.items.some((workflow) => workflow.id === workflowId) ? workflowId : ''}
                  onChange={(event) => setParams({ agencyId: String(agencyId), workflowId: event.target.value })}
                >
                  <MenuItem value="">{t('workflowPerformance.choose')}</MenuItem>
                  {workflows.data?.items.map((workflow) => (
                    <MenuItem key={workflow.id} value={workflow.id}>
                      {workflow.name}
                    </MenuItem>
                  ))}
                </TextField>
                {(page > 1 || (workflows.data?.count ?? 0) > 100) && (
                  <Stack direction="row">
                    <Button disabled={page === 1 || workflows.isFetching} onClick={() => setPage(page - 1)}>
                      {t('workflowPerformance.previous')}
                    </Button>
                    <Button
                      disabled={page * 100 >= (workflows.data?.count ?? 0) || workflows.isFetching}
                      onClick={() => setPage(page + 1)}
                    >
                      {t('workflowPerformance.next')}
                    </Button>
                  </Stack>
                )}
              </Stack>
            ))}
        </Box>
        {validAgency && !workflows.isError && workflowId && (
          <PerformanceDashboard
            agencyName={agencies.data?.find((agency) => agency.id === agencyId)?.name}
            key={`${myProfile?.id}:${agencyId}:${workflowId}`}
            agencyId={agencyId}
            workflowId={workflowId}
          />
        )}
      </Stack>
    </Box>
  );
}

export function PerformanceHome() {
  const { myProfile } = useAuth();
  return <PerformanceHomeScreen key={myProfile?.id} />;
}
